import EditorContent from "../common/EditorContent";
import { appendVariantLineItems, sortedVariantLineItems, variantLineItemIdentity, variantLineItemsMap } from "../../helper_functions/variantLineItems";
import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import {
  Button,
  LegacyCard,
  FormLayout,
  Icon,
  Page,
  LegacyStack,
  TextField,
  Layout,
  Select,
  PageActions,
  IndexTable,
  EmptyState,
  IndexFilters,
  useSetIndexFiltersMode,
} from "@shopify/polaris";
import { XIcon, ImportIcon, ExportIcon } from "@shopify/polaris-icons";
import AddVariantDialog from "../common/AddVariantDialog";
import DeleteConfirmationDialog from "../common/DeleteConfirmationDialog";
import HorseVariant from "../common/HorseVariant/HorseVariant";
import {
  deleteSupplier,
  updateSupplier,
  importSupplierHorseVariants,
  downloadFile,
  getSupplier,
  useUser,
  useCurrencies,
} from "../../api_utils/requests";
import { ErrorBanner } from "../common/ErrorBanner";
import { ImportFile } from "../common/ImportFile";
import { Footer } from "../common/Footer";
import {
  assertString,
  cloneNestedObjects,
  extractMessageFromError,
  getCurrencySymbol,
  getIdFromPath,
  softAssertNumber,
} from "../../helper_functions/utils";
import type { Supplier, SupplierHorseVariant, SupplierSelectedLineItem, SupplierResponse, LineItemVariant } from "../../api_utils/types";
import type { IndexTableHeading } from "@shopify/polaris/build/ts/src/components/IndexTable";
import type { SortButtonChoice } from "@shopify/polaris";
import type { NonEmptyArray } from "@shopify/polaris/build/ts/src/types";
import { SupplierShowPageSkeleton } from "../common/skeletons";
import { SUPPLIERS_HELP_PAGE_URL } from "../../constants";
import SaveBar from "../common/SaveBar";
import { useNavigate } from "react-router-dom";

type SupplierHorseVariantWithSortIndex = SupplierHorseVariant & { sortIndex: number };
type SupplierHorseVariants = Record<number, SupplierHorseVariantWithSortIndex>;

const formatSupplierToSave = ({
  name,
  email,
  currency,
  notes,
  minimum_order_value,
  delivery_lead_days,
}: Supplier): Partial<Supplier> => ({
  name,
  email,
  currency,
  notes,
  minimum_order_value,
  delivery_lead_days: delivery_lead_days === 0 ? undefined : delivery_lead_days,
});

const columnNames: NonEmptyArray<IndexTableHeading> = [
  { title: "Variant" },
  { title: "Minimum order quantity" },
  { title: "Pack size", tooltipContent: "Case quantity" },
  { title: "Supplier SKU" },
  { title: "Supplier cost" },
  { title: "" },
];

const sortOptions = [
  { label: "Created at", directionLabel: "Oldest/Newest", value: "created_at asc" },
  { label: "Created at", directionLabel: "Newest/Oldest", value: "created_at desc" },
  { label: "Title", directionLabel: "A-Z", value: "title asc" },
  { label: "Title", directionLabel: "Z-A", value: "title desc" },
  { label: "MOQ", directionLabel: "Low/High", value: "minimum_order_quantity asc" },
  { label: "MOQ", directionLabel: "High/Low", value: "minimum_order_quantity desc" },
  { label: "Pack size", directionLabel: "Low/High", value: "pack_size asc" },
  { label: "Pack size", directionLabel: "High/Low", value: "pack_size desc" },
  { label: "Supplier SKU", directionLabel: "A-Z", value: "supplier_sku asc" },
  { label: "Supplier SKU", directionLabel: "Z-A", value: "supplier_sku desc" },
  { label: "Supplier cost", directionLabel: "Low/High", value: "cost asc" },
  { label: "Supplier cost", directionLabel: "High/Low", value: "cost desc" },
] as SortButtonChoice[];

function ShowSupplier(): React.JSX.Element {
  const supplierId = useMemo(() => getIdFromPath(), []);

  const [importCSVModalIsOpen, setImportCSVModalIsOpen] = useState(false);

  const [supplier, setSupplier] = useState<Supplier>({} as Supplier);
  const originalSupplier = useRef<Supplier>();

  const originalSupplierHorseVariants = useRef<SupplierHorseVariants>({} as SupplierHorseVariants);
  const [supplierHorseVariants, setSupplierHorseVariants] = useState<SupplierHorseVariants>(
    {} as SupplierHorseVariants,
  );

  const savedSelectedLineItems = useRef<Record<number, SupplierSelectedLineItem>>({});
  const savedSelectedVariantIds = useRef<number[]>([]);
  const [selectedVariantIds, setSelectedVariantIds] = useState<number[]>([]);

  const [pageLoading, setPageLoading] = useState(true);
  const [variantsLoading, setVariantsLoading] = useState(true);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    shopify.loading(loading || pageLoading || variantsLoading);
  }, [loading, pageLoading, variantsLoading]);
  const [isDirty, setIsDirty] = useState(false);
  const [deletionDialogueActive, setDeletionDialogueActive] = useState(false);

  const [addVariantModalActive, setAddVariantModalActive] = useState(false);

  const [errorMessage, setErrorMessage] = useState("");

  // Pagination for SupplierHorseVariants
  const [hasNext, setHasNext] = useState<boolean>(false);
  const [hasPrev, setHasPrev] = useState<boolean>(false);
  const urlParams = new URLSearchParams(window.location.search);
  const [pageNum, setPageNum] = useState<number>(Number(urlParams.get("page")) || 1);
  const initSortedCol = urlParams.get("by_sort[column]") || "created_at";
  const initSortedColDir = urlParams.get("by_sort[direction]") || "asc";
  const [sort, setSort] = useState([`${initSortedCol} ${initSortedColDir}`]);
  const { mode, setMode } = useSetIndexFiltersMode();

  const setSupplierHorseVariantsData = (
    newSupplierHorseVariants: SupplierHorseVariant[],
    newHasNext: boolean,
    newHasPrev: boolean,
  ): void => {
    const formattedSupplierHorseVariants = variantLineItemsMap(newSupplierHorseVariants || []);
    setSupplierHorseVariants(formattedSupplierHorseVariants);
    originalSupplierHorseVariants.current = cloneNestedObjects(formattedSupplierHorseVariants);
    setHasNext(newHasNext);
    setHasPrev(newHasPrev);
  };

  const {
    data: {
      user: { currency: userCurrency },
    },
  } = useUser();

  const { data: currencies } = useCurrencies();

  const acceptResponse = useCallback((response: SupplierResponse): void => {
    savedSelectedLineItems.current = variantLineItemsMap(response.selected_line_items);
    savedSelectedVariantIds.current = response.selected_line_items.map(({ horse_variant_id }) => horse_variant_id);
    setSelectedVariantIds(savedSelectedVariantIds.current);
    setSupplier(response.supplier);
    originalSupplier.current = { ...response.supplier };
    setSupplierHorseVariantsData(response.line_items.rows, response.line_items.hasNext, response.line_items.hasPrev);
    setIsDirty(false);
  }, []);

  const query = useMemo(() => {
    const [column, direction] = sort[0].split(" ");
    return { page: pageNum, by_sort: { column, direction } };
  }, [pageNum, sort]);

  const refreshSavedVariants = useCallback(async (): Promise<void> => {
    setVariantsLoading(true);
    try {
      const response = await getSupplier(supplierId, query);
      acceptResponse(response);
    } catch (error: unknown) {
      setErrorMessage(extractMessageFromError(error));
    } finally {
      setPageLoading(false);
      setVariantsLoading(false);
    }
  }, [supplierId, query, acceptResponse]);

  useEffect(() => {
    void refreshSavedVariants();
  }, [refreshSavedVariants]);

  const addVariants = (horseVariants: LineItemVariant[]): void => {
    const addedLines = variantLineItemsMap(horseVariants.map((horseVariant) => originalSupplierHorseVariants.current[horseVariant.id] || {
      ...horseVariant,
      ...savedSelectedLineItems.current[horseVariant.id] || {
        id: null, horse_variant_id: horseVariant.id, supplier_id: supplierId,
        minimum_order_quantity: 0, pack_size: null, supplier_sku: null, cost: null,
      },
    }));
    setSupplierHorseVariants((lineItems) => appendVariantLineItems(lineItems, addedLines));
    setSelectedVariantIds((ids) => [...new Set([...ids, ...horseVariants.map(({ id }) => id)])]);
    setIsDirty(true);
  };

  const handleSupplierChange = useCallback((value: string, key: string): void => {
    setSupplier((prevSupplier) => {
      return { ...prevSupplier, [key]: value };
    });
    setIsDirty(true);
  }, []);

  const handleSupplierNumberChange = useCallback((value: string, key: string): void => {
    const cleanValue = value?.replace(/[^\d.-]/g, "");
    setSupplier((prevSupplier) => {
      return { ...prevSupplier, [key]: cleanValue };
    });
    setIsDirty(true);
  }, []);

  const handleSaveSupplier = useCallback(() => {
    setLoading(true);
    const formattedSupplier = formatSupplierToSave(supplier);
    const selectedIds = new Set(selectedVariantIds);
    const payload = {
      supplier: {
        ...formattedSupplier,
        supplier_horse_variants_attributes: sortedVariantLineItems(supplierHorseVariants).filter(({ horse_variant_id }) => selectedIds.has(horse_variant_id)).map((variant) => ({
          ...variantLineItemIdentity(variant),
          pack_size: variant.pack_size || null,
          minimum_order_quantity: variant.minimum_order_quantity || 0,
          supplier_sku: variant.supplier_sku || null,
          cost: variant.cost || null,
        })),
        removed_horse_variant_ids: savedSelectedVariantIds.current.filter((id) => !selectedIds.has(id)),
      },
    };
    const [column, direction] = sort[0].split(" ");
    return updateSupplier(supplierId, payload, { page: pageNum, by_sort: { column, direction } })
      .then((res) => {
        shopify.toast.show("Save successful");
        acceptResponse(res);
      })
      .catch((err: unknown) => {
        Rollbar.error(err, payload);
        const message = extractMessageFromError(err);
        setErrorMessage(message);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [supplier, supplierHorseVariants, supplierId, selectedVariantIds, pageNum, sort, acceptResponse]);

  const handleHorseVariantsChange = useCallback(
    (variantId: number, value: number | string, key: string) => {
      const newSupplierHorseVariants = { ...supplierHorseVariants };
      newSupplierHorseVariants[variantId] = {
        ...newSupplierHorseVariants[variantId],
        [key]: value,
      } as SupplierHorseVariantWithSortIndex;
      setSupplierHorseVariants(newSupplierHorseVariants);
      setIsDirty(true);
    },
    [supplierHorseVariants],
  );

  const removeVariants = (horseVariantIds: number[]): void => {
    const removedIds = new Set(horseVariantIds);
    setSupplierHorseVariants((lineItems) => Object.fromEntries(
      Object.entries(lineItems).filter(([id]) => !removedIds.has(Number(id))),
    ));
    setSelectedVariantIds((ids) => ids.filter((id) => !removedIds.has(id)));
    setIsDirty(true);
  };

  const handleDiscardAction = useCallback(() => {
    setSupplier({...originalSupplier.current});
    setSupplierHorseVariants(cloneNestedObjects(originalSupplierHorseVariants.current));
    setSelectedVariantIds(savedSelectedVariantIds.current);
    setIsDirty(false);
  }, [originalSupplier, originalSupplierHorseVariants]);

  const setPageNumAndScroll = useCallback((newPageNum: number) => {
    setVariantsLoading(true);
    setPageNum(newPageNum);
    document.getElementsByClassName("Polaris-IndexTable__Table")[0]?.scrollIntoView();
  }, []);
  const nextPage = useCallback(() => {
    if (hasNext && !isDirty) {
      setPageNumAndScroll(pageNum + 1);
    }
  }, [hasNext, isDirty, pageNum, setPageNumAndScroll]);
  const prevPage = useCallback(() => {
    if (hasPrev && !isDirty) {
      setPageNumAndScroll(pageNum - 1);
    }
  }, [hasPrev, isDirty, pageNum, setPageNumAndScroll]);

  const emptyState = (
    <EmptyState
      action={{
        content: "Add variants",
        onAction: () => {
          setAddVariantModalActive(true);
        },
      }}
      heading="No variants added"
      image=""
    >
      <p>You haven&apos;t added any variants to your supplier yet.</p>
    </EmptyState>
  );

  const navigate = useNavigate();

  const sortedSupplierHorseVariants = sortedVariantLineItems(supplierHorseVariants);

  const pageMarkup = supplier && (
    <Page
      actionGroups={[
        {
          title: "More actions",
          actions: [
            {
              content: "Export CSV",
              onAction: async (): Promise<void> => {
                const supplierName = supplier.name || supplierId;
                const filename = `Horse Supplier ${supplierName}.csv`;
                await downloadFile(`/suppliers/${supplierId}.csv${window.location.search}`, filename);
              },
              icon: ExportIcon,
            },
            {
              content: "Import CSV",
              disabled: isDirty,
              onAction: () => {
                setImportCSVModalIsOpen(true);
              },
              icon: ImportIcon,
            },
          ],
        },
      ]}
      backAction={{
        content: "Suppliers",
        // url: "/suppliers",
        onAction: async () => {
          await shopify.saveBar.leaveConfirmation();

          navigate("/suppliers");
        },
      }}
      title={supplier.name}
    >
      <SaveBar disabled={loading || variantsLoading} handleDiscard={handleDiscardAction} handleSave={handleSaveSupplier} isDirty={isDirty} />

      <ImportFile
        apiCallMethod={async (formData: object) => importSupplierHorseVariants(supplierId, formData)}
        modalIsOpen={importCSVModalIsOpen}
        setApiResponse={refreshSavedVariants}
        setErrorMessage={setErrorMessage}
        setModalIsOpen={setImportCSVModalIsOpen}
        title="Import supplier CSV"
      />

      <Layout>
        {errorMessage ? (
          <Layout.Section variant="fullWidth">
            <ErrorBanner errorMessage={errorMessage} setErrorMessage={setErrorMessage} />
          </Layout.Section>
        ) : null}
        <Layout.Section>
          <LegacyCard>
            <LegacyCard.Section>
              <FormLayout>
                <TextField
                  autoComplete="on"
                  id="name"
                  label="Name"
                  onChange={handleSupplierChange}
                  value={supplier.name}
                />
                <TextField
                  autoComplete="on"
                  id="email"
                  label="Contact email"
                  onChange={handleSupplierChange}
                  placeholder="contact@example.com"
                  value={assertString(supplier.email)}
                />
                <TextField
                  autoComplete="on"
                  id="notes"
                  label="Purchase order notes"
                  multiline={2}
                  onChange={handleSupplierChange}
                  value={assertString(supplier.notes)}
                />
                <FormLayout.Group condensed>
                  <TextField
                    autoComplete="off"
                    helpText="Used as the default arrival date when creating new purchase orders"
                    id="delivery_lead_days"
                    label="Delivery lead days"
                    min={0}
                    onChange={handleSupplierNumberChange}
                    suffix="days"
                    type="number"
                    value={assertString(supplier.delivery_lead_days)}
                  />
                  <TextField
                    autoComplete="on"
                    id="minimum_order_value"
                    label="Minimum Order Value"
                    onChange={handleSupplierNumberChange}
                    suffix={supplier.currency || userCurrency}
                    type="number"
                    value={assertString(supplier.minimum_order_value)}
                  />
                  <Select
                    id="currency"
                    label="Currency"
                    onChange={handleSupplierChange}
                    options={currencies}
                    value={supplier.currency || userCurrency}
                  />
                </FormLayout.Group>
              </FormLayout>
            </LegacyCard.Section>

            <LegacyCard.Section title="Add variants">
              <LegacyStack distribution="center">
                <AddVariantDialog
                  active={addVariantModalActive}
                  disabled={loading || variantsLoading}
                  onAdd={addVariants}
                  onRemove={removeVariants}
                  primary
                  selectedVariantIds={selectedVariantIds}
                  setActive={setAddVariantModalActive}
                  setErrorMessage={setErrorMessage}
                />
              </LegacyStack>
            </LegacyCard.Section>
          </LegacyCard>
        </Layout.Section>
        <Layout.Section>
          <LegacyCard>
            <IndexFilters
              disableQueryField
              disabled={isDirty}
              filters={[]}
              hideFilters
              hideQueryField
              mode={mode}
              onClearAll={() => {}}
              onQueryChange={() => {}}
              onQueryClear={() => {}}
              onSort={setSort}
              selected={0}
              setMode={setMode}
              sortOptions={sortOptions}
              sortSelected={sort}
              tabs={[]}
            />
            <IndexTable
              emptyState={emptyState}
              headings={columnNames}
              itemCount={sortedSupplierHorseVariants.length}
              loading={variantsLoading}
              pagination={{
                hasPrevious: hasPrev && !isDirty,
                previousKeys: [37],
                onPrevious: prevPage,
                hasNext: hasNext && !isDirty,
                nextKeys: [39],
                onNext: nextPage,
              }}
              selectable={false}
            >
              {sortedSupplierHorseVariants.map((variant: SupplierHorseVariantWithSortIndex, index) => (
                <IndexTable.Row id={variant.horse_variant_id.toString()} key={variant.horse_variant_id} position={index}>
                  <IndexTable.Cell>
                    <div className="medium_hv">
                      <HorseVariant key={variant.horse_variant_id} variant={variant} />
                    </div>
                  </IndexTable.Cell>
                  <IndexTable.Cell>
                    <div className="w120">
                      <TextField
                        autoComplete="off"
                        disabled={loading || variantsLoading || variant.discarded}
                        id={`minimum_order_quantity_${variant.horse_variant_id}`}
                        label="Minimum order quantity"
                        labelHidden
                        min={1}
                        name="Minimum order quantity"
                        onChange={(value: string) => {
                          handleHorseVariantsChange(variant.horse_variant_id, softAssertNumber(value), "minimum_order_quantity");
                        }}
                        type="number"
                        value={assertString(variant.minimum_order_quantity)}
                      />
                    </div>
                  </IndexTable.Cell>
                  <IndexTable.Cell>
                    <div className="w120">
                      <TextField
                        autoComplete="off"
                        disabled={loading || variantsLoading || variant.discarded}
                        id={`pack_size_${variant.horse_variant_id}`}
                        label="Pack size"
                        labelHidden
                        min={0}
                        name="Pack size"
                        onChange={(value: string) => {
                          handleHorseVariantsChange(variant.horse_variant_id, softAssertNumber(value), "pack_size");
                        }}
                        type="number"
                        value={assertString(variant.pack_size)}
                      />
                    </div>
                  </IndexTable.Cell>
                  <IndexTable.Cell>
                    <div className="w120">
                      <TextField
                        autoComplete="off"
                        disabled={loading || variantsLoading || variant.discarded}
                        id={`supplier_sku_${variant.horse_variant_id}`}
                        label="Supplier SKU"
                        labelHidden
                        name="Supplier SKU"
                        onChange={(value: string) => {
                          handleHorseVariantsChange(variant.horse_variant_id, value, "supplier_sku");
                        }}
                        value={assertString(variant.supplier_sku)}
                      />
                    </div>
                  </IndexTable.Cell>
                  <IndexTable.Cell>
                    <div className="w120">
                      <TextField
                        autoComplete="off"
                        disabled={loading || variantsLoading || variant.discarded}
                        id={`cost_${variant.horse_variant_id}`}
                        label="Supplier cost"
                        labelHidden
                        min={0.0}
                        name="Supplier cost"
                        onChange={(value: string) => {
                          handleHorseVariantsChange(variant.horse_variant_id, value, "cost");
                        }}
                        prefix={getCurrencySymbol(supplier.currency || userCurrency)}
                        step={0.01}
                        type="currency"
                        value={assertString(variant.cost)}
                      />
                    </div>
                  </IndexTable.Cell>
                  <IndexTable.Cell>
                    <Button
                      icon={<Icon source={XIcon} />}
                      onClick={() => {
                        removeVariants([variant.horse_variant_id]);
                      }}
                      variant="plain"
                    />
                  </IndexTable.Cell>
                </IndexTable.Row>
              ))}
            </IndexTable>
          </LegacyCard>
        </Layout.Section>
      </Layout>
      <PageActions
        secondaryActions={[
          {
            content: "Delete",
            destructive: true,
            onAction: () => {
              setDeletionDialogueActive(true);
            },
          },
        ]}
      />
      <DeleteConfirmationDialog
        active={deletionDialogueActive}
        content="Are you sure you want to delete this supplier? This action cannot be reversed."
        deleteItem={deleteSupplier}
        gotoPath="/suppliers"
        itemId={supplierId}
        setActive={setDeletionDialogueActive}
        title="Delete supplier"
        toastMessage="Supplier deleted"
      />
      <Footer pageTitle="suppliers" url={SUPPLIERS_HELP_PAGE_URL} />
    </Page>
  );

  return pageLoading ? <SupplierShowPageSkeleton /> : <EditorContent disabled={loading || variantsLoading}>{pageMarkup}</EditorContent>;
}

export default ShowSupplier;
